import React, { useState, useContext } from "react";
import { CustomList, CustomTag, CustomModal, CustomPageHeader } from "../../../ui";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/fr";
import { PlusIcon, TestTubeIcon } from "@phosphor-icons/react";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import toastOptions from "../../../assets/constants/toast";
import { AuthContext } from "../../../contexts";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("fr");
dayjs.tz.setDefault("Europe/Paris");

const TestList = () => {
    const { token } = useContext(AuthContext);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [test, setTest] = useState({
        name: "",
        hours: 1,
        minutes: 0,
        startDate: dayjs().tz("Europe/Paris").format("YYYY-MM-DD"),
        stopDate: dayjs().tz("Europe/Paris").add(1, "month").format("YYYY-MM-DD"),
        subjectLink: "",
    });

    const columns = React.useMemo(
        () => [
            {
                Header: "Nom du test",
                accessor: "name",
                icon: "pen",
                Cell: ({ cell: { value } }) => value || "",
            },
            {
                Header: "Début du test",
                accessor: "startDate",
                icon: "calendar",
                Cell: ({ cell: { value } }) => (
                    <CustomTag value={dayjs(value).tz("Europe/Paris").format("DD MMMM YYYY")} />
                ),
            },
            {
                Header: "Fin du test",
                accessor: "stopDate",
                icon: "calendar",
                Cell: ({ cell: { value } }) => (
                    <CustomTag value={dayjs(value).tz("Europe/Paris").format("DD MMMM YYYY")} />
                ),
            },
        ],
        []
    );

    const handleOpenModal = () => {
        setTest({
            name: "",
            hours: 1,
            minutes: 0,
            startDate: dayjs().tz("Europe/Paris").format("YYYY-MM-DD"),
            stopDate: dayjs().tz("Europe/Paris").add(1, "month").format("YYYY-MM-DD"),
            subjectLink: "",
        });
        setIsModalOpen(true);
    };

    const handleCreateTest = async () => {
        if (!test.name || !test.subjectLink) {
            toast.error("Veuillez remplir tous les champs obligatoires", toastOptions);
            return;
        }
        
        setIsCreating(true);
        try {
            await axios.post(process.env.REACT_APP_UPA_API_HOST + "tests", test, {
                headers: { Authorization: "Bearer " + token },
            });
            
            window.dispatchEvent(new CustomEvent("testsDataUpdated"));
            toast.success("Le test a bien été créé", toastOptions);
            setIsModalOpen(false);
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.message || "Erreur lors de la création", toastOptions);
        } finally {
            setIsCreating(false);
        }
    };

    const formatDuration = () => {
        const hours = test.hours || 0;
        const minutes = test.minutes || 0;
        
        if (hours === 0 && minutes === 0) return "Aucune durée définie";
        
        let duration = "";
        if (hours > 0) duration += `${hours}h`;
        if (minutes > 0) duration += `${hours > 0 ? ' ' : ''}${minutes}min`;
        
        return duration;
    };

    const isFormValid = test.name && test.subjectLink && test.startDate && test.stopDate;

    return (
        <div className="c-app__view">
            <CustomPageHeader 
                title="Tests"
                titleIcon={{ icon: TestTubeIcon }}
                buttons={[
                    {
                        label: "Créer un test",
                        onClick: handleOpenModal,
                        icon: PlusIcon,
                        variant: "primary"
                    }
                ]}
            />
            <div className="tabs-container">
                <CustomList entity={"tests"} columns={columns} />
            </div>

            <CustomModal show={isModalOpen} setShow={setIsModalOpen} title="Nouveau test de candidature">
                <div className="custom__modal-group">
                    <label className="name">Nom du test *</label>
                    <input
                        type="text"
                        placeholder="Exemple : Test développeur front-end"
                        value={test.name}
                        onChange={(e) => setTest({ ...test, name: e.target.value })}
                        className="classic-input"
                    />
                </div>

                <div className="custom__modal-group">
                    <label className="date">Dates de validité *</label>
                    <div className="test-add__date-group">
                        <div className="test-add__date-icon"></div>
                        <span className="test-add__text">Du</span>
                        <input
                            type="date"
                            className="classic-input"
                            value={test.startDate}
                            onChange={(e) => setTest({ ...test, startDate: e.target.value })}
                        />
                        <span className="test-add__date-separator">au</span>
                        <input
                            type="date"
                            className="classic-input"
                            value={test.stopDate}
                            min={test.startDate}
                            onChange={(e) => setTest({ ...test, stopDate: e.target.value })}
                        />
                    </div>
                </div>

                <div className="custom__modal-group">
                    <label className="hourglass">Durée du test *</label>
                    <div className="test-add__duration-group">
                        <div className="test-add__duration-icon"></div>
                        <input
                            type="number"
                            className="classic-input test-add__duration-input"
                            min={0}
                            max={10}
                            step={1}
                            value={test.hours}
                            onChange={(e) => setTest({ ...test, hours: parseInt(e.target.value) || 0 })}
                            placeholder="0"
                        />
                        <span className="test-add__text">heure(s) et</span>
                        <input
                            type="number"
                            className="classic-input test-add__duration-input"
                            min={0}
                            max={59}
                            step={15}
                            value={test.minutes}
                            onChange={(e) => setTest({ ...test, minutes: parseInt(e.target.value) || 0 })}
                            placeholder="0"
                        />
                        <span className="test-add__text">minute(s)</span>
                        <div className="test-add__duration-preview">
                            <CustomTag value="duration" label={formatDuration()} />
                        </div>
                    </div>
                </div>

                <div className="custom__modal-group">
                    <label className="link-blue">Lien du sujet *</label>
                    <input
                        type="url"
                        placeholder="https://notion.so/votre-sujet-de-test"
                        value={test.subjectLink}
                        onChange={(e) => setTest({ ...test, subjectLink: e.target.value })}
                        className="classic-input"
                    />
                </div>

                <div className="test-add__info-banner">
                    <div className="test-add__info-icon"></div>
                    <div className="test-add__info-text">
                        <strong>Information :</strong> Les candidats pourront s'inscrire et passer le test uniquement pendant la période de validité définie.
                    </div>
                </div>

                <div className="modal-buttons">
                    <button 
                        onClick={() => setIsModalOpen(false)} 
                        className="button-large cancel-button" 
                        disabled={isCreating}
                    >
                        Annuler
                    </button>
                    <button 
                        onClick={handleCreateTest} 
                        className="button-large blue-button validate" 
                        disabled={isCreating || !isFormValid}
                    >
                        {isCreating ? (
                            <CircularProgress size={16} className="modal-progress" />
                        ) : (
                            <PlusIcon size={16} />
                        )}
                        <span>{isCreating ? "Création..." : "Créer le test"}</span>
                    </button>
                </div>
            </CustomModal>
        </div>
    );
};

export default TestList;
